"use client"
import React, { useMemo, useState } from 'react'
import { motion, AnimatePresence } from "motion/react"
import { MMHeader, MMTabBar, MMFooter } from '../../components/MMChrome'
import { RedirectToPC } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { mmDict } from '@/lib/i18n/dicts/mm'
import { commonDict } from '@/lib/i18n/dicts/common'
import { categoryLabel, gameCover, type LocalizedGame } from '@/lib/game-i18n'
import { useRegion } from '@/app/hooks/use-region'
import type { Category } from '@/lib/categories'

// 封面：后台上传的列表图（/media/）优先，否则按中文名取 gamelistpic2 默认封面（lib/game-i18n.gameCover）
const coverFor = gameCover

// 内部筛选哨兵值（不展示，展示文案走字典 gamelist.all）
const ALL_CAT = '所有游戏'

export default function MMGamelistClient({ games: allGames, categories: managedCats, locale }: { games: LocalizedGame[]; categories: Category[]; locale: Locale }) {
    const [activeCat, setActiveCat] = useState(ALL_CAT)
    // 大陆 IP 判定（统一走 useRegion；?isoversea=1 跳过），用于模糊"海外"游戏
    const currentisCN = useRegion() === 'cn'
    const t = pick(mmDict, locale).gamelist
    const common = pick(commonDict, locale)

    // 分类 chips：以分类管理中的顺序为准；游戏引用了未登记分类时追加在最后，保证游戏不丢
    const categories = useMemo(() => {
        const managedNames = managedCats.map((c) => c.name)
        const leftover = Array.from(new Set(allGames.map((g) => g.catelog)))
            .filter((c) => !managedNames.includes(c))
            .map((name) => ({ name, name_en: '' }))
        return [...managedCats, ...leftover]
    }, [allGames, managedCats])

    // "海外"沉底，其余保持原顺序
    const games = useMemo(() => {
        const sorted = [...allGames].sort(
            (a, b) => (a.catelog === "海外" ? 1 : 0) - (b.catelog === "海外" ? 1 : 0)
        )
        return activeCat === ALL_CAT ? sorted : sorted.filter((g) => g.catelog === activeCat)
    }, [activeCat, allGames])

    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 max-w-md mx-auto relative shadow-2xl shadow-black">
            <MMHeader locale={locale} />
            <RedirectToPC href="/gamelist2" />

            {/* Hero */}
            <section className="relative px-5 pt-8 pb-5 overflow-hidden">
                <div className="pointer-events-none absolute -top-24 left-1/3 h-56 w-56 rounded-full bg-indigo-600/20 blur-[90px]" />
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                >
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30 px-3 py-1 text-[11px] font-medium text-indigo-300">
                        <span className="relative flex size-1.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex size-1.5 rounded-full bg-indigo-400"></span>
                        </span>
                        {t.badge.replace('{count}', String(allGames.length))}
                    </span>
                    <h1 className="mt-4 text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-white to-fuchsia-300">
                        {t.title}
                    </h1>
                    <p className="mt-2 text-xs leading-5 text-slate-400">
                        {t.desc}
                    </p>
                </motion.div>
            </section>

            {/* 分类 chips（横向滚动，吸顶于导航下方） */}
            <div className="sticky top-[53px] z-30 bg-slate-950/90 backdrop-blur-lg border-y border-white/5">
                <div className="flex gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {[{ name: ALL_CAT, name_en: '' }, ...categories].map((cat) => (
                        <button
                            key={cat.name}
                            onClick={() => setActiveCat(cat.name)}
                            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${activeCat === cat.name
                                ? "bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white shadow-lg shadow-indigo-600/30"
                                : "bg-white/5 ring-1 ring-white/10 text-slate-400"
                                }`}
                        >
                            {cat.name === ALL_CAT ? t.all : categoryLabel(cat, locale)}
                        </button>
                    ))}
                </div>
            </div>

            {/* 游戏列表 */}
            <section className="px-4 py-5 pb-6">
                <motion.div layout className="flex flex-col gap-4">
                    <AnimatePresence mode="popLayout">
                        {games.map((g) => (
                            <MMGameCard key={g.gameid} game={g} isCN={currentisCN} locale={locale} catText={categoryLabel(categories.find((c) => c.name === g.catelog) ?? { name: g.catelog, name_en: '' }, locale)} />
                        ))}
                    </AnimatePresence>
                </motion.div>
            </section>

            <MMFooter locale={locale} />
            <MMTabBar active="games" locale={locale} />
        </div>
    )
}

function MMGameCard({ game, isCN, locale, catText }: { game: LocalizedGame; isCN: boolean; locale: Locale; catText: string }) {
    const blocked = isCN && game.catelog === "海外"
    const t = pick(mmDict, locale).gamelist
    const common = pick(commonDict, locale)

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
        >
            <a
                href={blocked ? "#" : `/mm/gamedetail/${game.gameid}`}
                className="block rounded-2xl bg-slate-900 ring-1 ring-white/10 overflow-hidden active:scale-[0.98] transition-transform"
            >
                <div className="relative aspect-[4/3] overflow-hidden">
                    {blocked && (
                        <span className="absolute inset-0 z-20 flex items-center justify-center px-6 text-center text-xs font-bold text-white bg-slate-950/40">
                            {t.blocked}
                        </span>
                    )}
                    <img
                        src={coverFor(game)}
                        alt={game.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = game.imageUrl }}
                        className={`h-full w-full object-cover ${blocked ? "blur-md" : ""}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 rounded-full bg-indigo-500/80 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium text-white">
                        {catText}
                    </span>
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-slate-950/60 backdrop-blur px-2 py-0.5 text-[10px] text-slate-200 ring-1 ring-white/15">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`h-3 w-3 ${game.screen === "竖版" ? "" : "rotate-90"}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-16.5A2.25 2.25 0 0 0 15.75 1.5H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                        </svg>
                        {common.game.screen[game.screen] ?? game.screen}
                    </span>
                    <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                        <h3 className="text-lg font-bold text-white">{game.name}</h3>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 backdrop-blur px-2.5 py-1 text-[10px] text-white ring-1 ring-white/20">
                            {common.game.viewDetail}
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </span>
                    </div>
                </div>
                <div className="px-4 py-3">
                    <p className="text-xs leading-5 text-slate-400 line-clamp-2">
                        {game.一句话描述 || game.游戏简介}
                    </p>
                    {game.标签 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {game.标签.split('、').slice(0, 3).map((tag) => (
                                <span key={tag} className="rounded-md bg-white/5 ring-1 ring-white/10 px-2 py-0.5 text-[10px] text-slate-400">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </a>
        </motion.div>
    )
}
